import { Select as BaseSelect } from '@base-ui/react/select';
import { useRef, useState, type ReactNode } from 'react';

import { Dialog, type DialogProps } from '../Dialog';
import { Icon } from '../Icon';
import { Tag } from '../Tag';
import arrowStyles from '../shared/floating-arrow.module.css';
import styles from './Select.module.css';

export interface SelectOption {
  disabled?: boolean;
  description?: string;
  group?: string;
  label: string;
  value: string;
}

export interface SelectDialogContext {
  close: () => void;
  select: (value: string) => void;
  value: string;
}

export type SelectSize = 'large' | 'default' | 'small';

interface SelectCoreProps {
  /** Ant Design `allowClear`：有选中值时悬停或聚焦显示清空按钮，键盘可达。 */
  allowClear?: boolean;
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  dialogContent?: (context: SelectDialogContext) => ReactNode;
  dialogSize?: DialogProps['size'];
  dialogTitle?: string;
  /** Figma Error 状态：边框转为危险色，优先级高于悬停与聚焦。 */
  error?: boolean;
  id?: string;
  /** Ant Design `loading`：浮层内显示加载状态。 */
  loading?: boolean;
  mode?: 'dropdown' | 'dialog' | 'external';
  name?: string;
  /** Ant Design `notFoundContent`：没有可选项时的占位文案，默认「暂无数据」。 */
  notFoundContent?: string;
  onOpen?: () => void;
  options: readonly SelectOption[];
  /** Ant Design `placeholder`：没有选中值时的占位文案。 */
  placeholder?: string;
  size?: SelectSize;
}

export interface SelectProps extends SelectCoreProps {
  maxTagCount?: never;
  multiple?: false;
  onValueChange: (value: string) => void;
  value: string;
}

/** Ant Design `mode="multiple"`：值为数组，浮层不随选择关闭，选项带勾选图标。 */
export interface SelectMultipleProps extends SelectCoreProps {
  /** Ant Design `maxTagCount`：超出数量折叠为 `+N`（Figma Collapse Tags）。 */
  maxTagCount?: number;
  multiple: true;
  onValueChange: (value: string[]) => void;
  value: string[];
}

export type SelectAnyProps = SelectProps | SelectMultipleProps;

interface SelectGroupBlock {
  label?: string;
  options: SelectOption[];
}

function toGroupBlocks(options: readonly SelectOption[]): SelectGroupBlock[] {
  const blocks: SelectGroupBlock[] = [];

  for (const option of options) {
    const last = blocks.at(-1);

    if (last && last.label === option.group) {
      last.options.push(option);
    } else {
      blocks.push({ label: option.group, options: [option] });
    }
  }

  return blocks;
}

export function Select(props: SelectAnyProps) {
  const {
    allowClear = false,
    ariaLabel,
    className,
    disabled = false,
    error = false,
    id,
    loading = false,
    mode = 'dropdown',
    name,
    notFoundContent = '暂无数据',
    options,
    placeholder,
    size = 'default',
  } = props;
  const multiple = props.multiple === true;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popupMinWidth, setPopupMinWidth] = useState<number>();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const selectedValues = multiple ? props.value : props.value === '' ? [] : [props.value];
  const selectedOptions = selectedValues
    .map((item) => options.find((option) => option.value === item))
    .filter((option): option is SelectOption => Boolean(option));
  const maxTagCount = multiple ? props.maxTagCount : undefined;
  const overflowCount = maxTagCount && selectedOptions.length > maxTagCount ? selectedOptions.length - maxTagCount : 0;
  const visibleOptions = overflowCount > 0 ? selectedOptions.slice(0, maxTagCount) : selectedOptions;
  /* dialog / external 与 dropdown 一致：空字符串也能命中 `value: ''` 的选项（如「不限」）。 */
  const selectedLabel = options.find((option) => option.value === props.value)?.label;
  const hasValue = multiple ? selectedValues.length > 0 : props.value !== '';
  const clearable = allowClear && !disabled && hasValue;
  /*
   * dropdown / multiple 的最外层是 `.root` 包装层（清空按钮要相对它绝对定位），
   * 所以页面传入的 className 必须落在这一层：它是 flex/block 布局里的那个「盒子」，
   * 宽度类（如 `width: 220px`）只有挂在这里才不会把同排的 label 文字挤成竖排。
   * external / dialog 没有包装层，className 仍落在按钮上。
   */
  const rootClassName = [styles.root, className].filter(Boolean).join(' ');
  const controlClassName = [styles.control, className].filter(Boolean).join(' ');
  const iconSize = size === 'small' ? 'xs' : 'sm';
  const emitValue = (next: string | string[]) => {
    if (multiple) {
      (props as SelectMultipleProps).onValueChange(next as string[]);
    } else {
      (props as SelectProps).onValueChange(next as string);
    }
  };
  const handleOpenChange = (open: boolean) => {
    const width = triggerRef.current?.offsetWidth;
    setPopupMinWidth(open && width ? width : undefined);
  };
  const removeValue = (value: string) => {
    emitValue(selectedValues.filter((item) => item !== value));
  };
  const clearValue = () => {
    emitValue(multiple ? [] : '');
  };
  const selectFromDialog = (nextValue: string) => {
    emitValue(nextValue);
    setDialogOpen(false);
  };

  /* 多选触发器必须是 div：标签内还有移除按钮，button 里不能嵌套 button。 */
  const multipleTrigger = (
    <div className={styles.control} data-error={error || undefined} data-size={size} tabIndex={disabled ? -1 : 0}>
      {selectedOptions.length > 0 ? (
        <span className={styles.tags} onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()}>
          {visibleOptions.map((option) => (
            <Tag closable closeLabel={`移除${option.label}`} key={option.value} onClose={() => removeValue(option.value)} size="small" type="info">
              {option.label}
            </Tag>
          ))}
          {overflowCount > 0 ? <Tag className={styles.overflowTag} size="small" type="info">{`+${overflowCount}`}</Tag> : null}
        </span>
      ) : (
        <span className={styles.placeholder}>{placeholder}</span>
      )}
      <BaseSelect.Icon className={styles.icon}><Icon name="a-arrow-down" size={iconSize} /></BaseSelect.Icon>
    </div>
  );

  const renderItem = (option: SelectOption) => (
    <BaseSelect.Item className={styles.item} disabled={option.disabled} key={option.value} value={option.value}>
      <span className={styles.itemText}>
        <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
        {option.description ? <span className={styles.description}>{option.description}</span> : null}
      </span>
      {multiple && selectedValues.includes(option.value) ? <Icon className={styles.itemCheck} name="check" size={iconSize} /> : null}
    </BaseSelect.Item>
  );

  if (mode === 'external') {
    return (
      <button aria-haspopup="dialog" aria-label={ariaLabel} className={controlClassName} data-error={error || undefined} data-size={size} disabled={disabled} onClick={props.onOpen} type="button">
        <span className={selectedLabel ? styles.value : styles.placeholder}>{selectedLabel ?? placeholder}</span>
        <Icon className={styles.triggerIcon} name="a-arrow-down" size={iconSize} />
      </button>
    );
  }

  if (mode === 'dialog') {
    return (
      <>
        <button aria-expanded={dialogOpen} aria-haspopup="dialog" aria-label={ariaLabel} className={controlClassName} data-error={error || undefined} data-size={size} disabled={disabled} onClick={() => setDialogOpen(true)} type="button">
          <span className={selectedLabel ? styles.value : styles.placeholder}>{selectedLabel ?? placeholder}</span>
          <Icon className={styles.triggerIcon} name="a-arrow-down" size={iconSize} />
        </button>
        <Dialog onOpenChange={setDialogOpen} open={dialogOpen} size={props.dialogSize ?? 'md'} title={props.dialogTitle ?? ariaLabel}>
          {props.dialogContent?.({ close: () => setDialogOpen(false), select: selectFromDialog, value: props.value as string }) ?? null}
        </Dialog>
      </>
    );
  }

  const popupBody = loading ? (
    <p className={styles.status} role="status">
      <Icon className={styles.spinner} name="loop-left-fill" size={iconSize} />
      加载中…
    </p>
  ) : options.length === 0 ? (
    <p className={styles.status}>{notFoundContent}</p>
  ) : (
    <BaseSelect.List className={styles.list}>
      {toGroupBlocks(options).map((block, index) => (
        <BaseSelect.Group className={styles.group} key={block.label ?? index}>
          {block.label ? <BaseSelect.GroupLabel className={styles.groupLabel}>{block.label}</BaseSelect.GroupLabel> : null}
          {block.options.map(renderItem)}
        </BaseSelect.Group>
      ))}
    </BaseSelect.List>
  );

  return (
    <BaseSelect.Root
      disabled={disabled}
      id={id}
      items={options}
      multiple={multiple}
      name={name}
      onOpenChange={handleOpenChange}
      onValueChange={(nextValue, details) => {
        // Removing an imported option can temporarily unregister and reindex the
        // remaining items. Keep a controlled value that still exists in options;
        // this internal list update must not erase a user's committed selection.
        if (!multiple && nextValue === null && details.reason === 'none' && options.some((option) => option.value === props.value)) {
          details.cancel();
          return;
        }
        emitValue(multiple ? (nextValue as string[]) ?? [] : (nextValue as string) ?? '');
      }}
      value={props.value}
    >
      <div className={rootClassName} data-size={size}>
        {multiple ? (
          <BaseSelect.Trigger aria-label={ariaLabel} nativeButton={false} ref={triggerRef} render={multipleTrigger} />
        ) : (
          <BaseSelect.Trigger aria-label={ariaLabel} className={styles.control} data-error={error || undefined} data-size={size} ref={triggerRef}>
            <BaseSelect.Value className={styles.value} placeholder={placeholder ? <span className={styles.placeholder}>{placeholder}</span> : undefined} />
            <BaseSelect.Icon className={styles.icon}><Icon name="a-arrow-down" size={iconSize} /></BaseSelect.Icon>
          </BaseSelect.Trigger>
        )}
        {clearable ? (
          <button aria-label={`清空${ariaLabel}`} className={styles.clear} onClick={clearValue} onPointerDown={(event) => event.stopPropagation()} type="button">
            <Icon name="circle-close-filled" size={iconSize} />
          </button>
        ) : null}
      </div>
      <BaseSelect.Portal>
        <BaseSelect.Positioner align="start" alignItemWithTrigger={false} className={styles.positioner} sideOffset={6}>
          <BaseSelect.Popup aria-label={`${ariaLabel}选项`} className={styles.popup} style={popupMinWidth ? { minWidth: popupMinWidth } : undefined}>
            {popupBody}
            <span aria-hidden="true" className={[styles.arrow, arrowStyles.arrow].join(' ')} />
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}
